import api from "../config/axiosConfig";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { actionType } from "../utils/constant";

/**
 * Debounce a changing value.
 * Used for search input to avoid spamming the API.
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Painting templates UI controller.
 * - Owns list, selection, pagination, and delete.
 * - Exposes a formKey to force form remount/reset on each open.
 */
export default function usePaintTemplate() {
  const { profile } = useSelector((state) => state.profile);
  const { permissions } = profile;

  // -------------- STATE MANAGEMENT --------------
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createModalDialogue, setCreateModalDialogue] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [carausalCollapsed, setCarausalCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000); // 1000ms debounce delay
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSelectedModel, setDeleteSelectedModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [selectedTemplateLoading, setSelectedTemplateLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 5,
    totalPages: 1,
    total: 0,
  });

  // -------------- TEMPLATE ACTIONS --------------
  /**
   * After form save, close modal, clear selection and refresh list.
   */
  const handleSubmit = async () => {
    setCreateModalDialogue(false);
    setSelectedTemplate(null);
    setCarausalCollapsed(false);
    fetchTemplatesAPI();
  };

  /** Close the modal and clear selection. */
  const handleClose = async () => {
    setCreateModalDialogue(false);
    if (selectedTemplate) {
      await fetchTemplateDetail(
        selectedTemplate.painting_checklist_template_id
      );
    }
  };

  /** Delete selected template with loader and refresh list. */
  const onTemplateDelete = async () => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete this template");
      return;
    }
    setDeleting(true);
    try {
      await deleteTemplateAPI(deleteSelectedModel.painting_template_id);
      setDeleteModal(false);
      setSelectedTemplate(null);
      await fetchTemplatesAPI();
    } finally {
      setDeleting(false);
    }
  };

  /** Open create modal. Also bump formKey to reset the form. */
  const createTemplateClick = () => {
    if (!permissions.actions.includes(actionType.template_create)) {
      toast.error("You don't have permission to create this template");
      return;
    }
    setSelectedTemplate(null);
    setCreateModalDialogue(true);
    setFormKey((k) => k + 1);
  };

  /** Select a template row: fetch full template via API and collapse the list. */
  const onTemplateClick = async (template) => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view this template");
      return;
    }
    setSelectedTemplate({ model: template });
    await fetchTemplateDetail(template?.painting_template_id);
    setCarausalCollapsed(true);
  };

  /**
   * Fetch a template's full details from the API.
   * @param {number} id - Painting template ID to fetch.
   */
  const fetchTemplateDetail = async (id) => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view this template");
      return;
    }
    try {
      setSelectedTemplateLoading(true);
      const res = await api.get(`/api/painting-checklist-templates/${id}`);
      // Some endpoints return {data}, others return the object directly
      const detailed = res?.data?.data || res?.data || null;
      setSelectedTemplate(detailed);
    } catch (err) {
      console.error("Error fetching template detail:", err);
      toast.error(
        err?.response?.data?.message || "Failed to fetch template details"
      );
      setSelectedTemplate(null);
    } finally {
      setSelectedTemplateLoading(false);
    }
  };

  // Open edit modal and bump formKey so form re-initializes with selected data.
  const onEditTemplateClick = () => {
    if (!permissions.actions.includes(actionType.template_edit)) {
      toast.error("You don't have permission to edit this template");
      return;
    }
    setCreateModalDialogue(true);
    setFormKey((k) => k + 1);
  };
  /** Update current page. */
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  /** Map raw API templates to table rows. */
  const tableData = useMemo(() => {
    return allTemplates?.map((data) => {
      return {
        painting_template_id: data?.painting_checklist_template_id || "",
        model_name: data?.model?.model_name || "",
        model_id: data?.model?.model_id || "",
        created_at: data?.created_at,
        no_of_items: data?.painting_checklist_template_parts?.length || 0,
      };
    });
  }, [allTemplates]);

  /**
   * Delete painting template
   *
   * @param {string} templateId - Template ID to delete
   * @returns {Promise} API response
   */
  const deleteTemplateAPI = async (templateId) => {
    if (!permissions.actions.includes(actionType.template_delete)) {
      toast.error("You don't have permission to delete this template");
      return;
    }
    return await api
      .delete(`/api/painting-checklist-templates/${templateId}`)
      .then((res) => {
        toast.success(res?.data?.message || "Template deleted successfully!");
        return res;
      })
      .catch((err) => {
        console.error("Error deleting template:", err);
        toast.error(err?.response?.data?.message || "Something went wrong!");
        return err;
      });
  };

  /**
   * Fetch painting templates with search and pagination
   *
   * @returns {Promise} API response
   */
  const fetchTemplatesAPI = useCallback(async () => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view this template");
      return;
    }
    setLoading(true);
    return await api
      .get(`/api/painting-checklist-templates/`, {
        params: {
          ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
          page: pagination.page,
          perPage: pagination.perPage,
        },
      })
      .then((res) => {
        // Set the raw data from API response
        const templateData = res?.data.data;
        setAllTemplates(templateData?.data);
        setPagination((prev) => ({
          ...prev,
          total: templateData.count,
          totalPages: templateData.totalPages,
          page: templateData.currentPage,
          perPage: templateData.perPage,
        }));
        setLoading(false);
        return res;
      })
      .catch((err) => {
        console.error("Error fetching templates:", err);
        setLoading(false);
        return err;
      });
  }, [pagination.page, pagination.perPage, debouncedSearchTerm, permissions]);

  // Fetch templates on mount and when search/pagination changes
  useEffect(() => {
    if (!permissions.actions.includes(actionType.template_view)) {
      toast.error("You don't have permission to view this template");
    } else {
      fetchTemplatesAPI();
    }
  }, [
    fetchTemplatesAPI,
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    permissions,
  ]);

  // -------------- EXPORTED INTERFACE --------------
  return {
    tableData,
    selectedTemplate,
    selectedTemplateLoading,
    createModalDialogue,
    allTemplates,
    carausalCollapsed,
    handleSubmit,
    onTemplateDelete,
    createTemplateClick,
    onTemplateClick,
    setCarausalCollapsed,
    setSearchTerm,
    pagination,
    handlePageChange,
    deleteModal,
    setDeleteModal,
    deleteSelectedModel,
    setDeleteSelectedModel,
    loading,
    deleting,
    formKey,
    fetchTemplatesAPI,
    handleClose,
    onEditTemplateClick,
    permissions,
  };
}
